import { useQuery } from '@tanstack/react-query';
import { useDependencies } from '@servicetitan/react-ioc';
import {
    Calculation,
    FinancingApi,
    LoansGetCalculationsData,
    LoansGetCalculationsParams,
} from '@servicetitan/financing-api-client';
import { AxiosResponse } from 'axios';
import { ServerError } from '@servicetitan/financing-utils-shared';
import { useLoanAmountValidation } from '../providers/useLoanAmountValidation';

/**
 * Gets the effective monthly payment for a calculation.
 * Uses the lower of defaultMonthlyPayment and suggestedMonthlyPayment (if it exists and is positive).
 */
const getEffectiveMonthlyPayment = (calc: Calculation): number => {
    const { defaultMonthlyPayment, suggestedMonthlyPayment } = calc;

    // If suggestedMonthlyPayment is null, undefined, or not positive, use defaultMonthlyPayment
    if (suggestedMonthlyPayment == null || suggestedMonthlyPayment <= 0) {
        return defaultMonthlyPayment;
    }

    // Return the lower of the two payments
    return Math.min(defaultMonthlyPayment, suggestedMonthlyPayment);
};

/**
 * Sorts calculations by:
 * 1. Effective monthly payment (ascending)
 * 2. Default rate (ascending) - as tie-breaker
 */
const sortCalculations = (calculations: Calculation[]): Calculation[] => {
    return [...calculations].sort((a, b) => {
        const aPayment = getEffectiveMonthlyPayment(a);
        const bPayment = getEffectiveMonthlyPayment(b);

        // Primary sort: by effective monthly payment (lowest first)
        if (aPayment !== bPayment) {
            return aPayment - bPayment;
        }

        // Secondary sort: by default rate (lowest first)
        return a.defaultRate - b.defaultRate;
    });
};

const selectEligiblePlans =
    // The first argument is an array of input selectors, telling it which pieces of the state we want to use
    // In this case we are not selecting from the redux state but from the API query result
    // We also need to use tenantSettings which is not part of the query result, but passed as an additional argument
    (response: AxiosResponse<LoansGetCalculationsData>) => {
        const eligiblePlans = response.data?.filter(calc => calc.eligibleForApplication);
        let minimumAmount: number = 0;
        let noPlansEligible: boolean = false;

        if (eligiblePlans?.length === 0 && response.data?.length > 0) {
            minimumAmount =
                response.data?.reduce(
                    (min, plan) => (plan.minLoanAmount! < min ? plan.minLoanAmount! : min),
                    Infinity,
                ) ?? 0;
            noPlansEligible = true;
        }

        // Sort eligible plans by effective monthly payment, then by default rate
        const sortedEligiblePlans = eligiblePlans ? sortCalculations(eligiblePlans) : eligiblePlans;

        return {
            data: sortedEligiblePlans,
            noPlansEligible,
            minimumAmount,
        };
    };

// Simple wrapper to get calculations and filter out ineligible plans based on tenant settings
export const useGetEligiblePlans = (params: LoansGetCalculationsParams) => {
    const [apiClient] = useDependencies(FinancingApi);
    const { isValidLoanAmount } = useLoanAmountValidation(params.amount);

    return useQuery<
        AxiosResponse<LoansGetCalculationsData>,
        ServerError,
        {
            data: Calculation[];
            noPlansEligible: boolean;
            minimumAmount: number;
        },
        [string, LoansGetCalculationsParams]
    >({
        queryKey: ['loansGetCalculations', params],
        queryFn: () => apiClient.loansGetCalculations(params),
        select: selectEligiblePlans,
        enabled: isValidLoanAmount,
    });
};
